import PropTypes from 'prop-types';
import React from 'react';

import {getLocationCapacityConfigColumns} from 'bulk-import/storables/StorableColumnsUtil';
import {Registry, REGISTRY_SHAPE} from 'constants/RegistryConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import LocationSchemaSelector from 'samples/LocationSchemaSelector';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import StorableImportSettingsModalPane from './panes/StorableImportSettingsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  STORABLE_BASE_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';

export interface BulkCreateLocationsFlowProps {
  initialRegistry?: Registry | null;
  onRequestClose: () => void;
  actionOrigin: ActionOrigin;
}

class BulkCreateLocationsFlow extends React.Component<BulkCreateLocationsFlowProps> {
  static propTypes = {
    initialRegistry: REGISTRY_SHAPE,
    onRequestClose: PropTypes.func.isRequired,
  };

  static open(props: Omit<BulkCreateLocationsFlowProps, 'onRequestClose'>): void {
    LegacyModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkCreateLocationsFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  _getPanes = (): Array<Pane> => [
    {
      paneKey: PaneKey.SET_STORABLE_IMPORT_SETTINGS,
      configuration: StorableImportSettingsModalPane.configure({
        schemaSelectorClass: LocationSchemaSelector,
        schemaIdAccessorKey: 'locationSchemaId',
        showProjectSelector: false,
      }),
    },
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({
        getExampleImageURL(): string {
          return '/static/img/spreadsheet-import-example-create-locations-v2.png';
        },
      }),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        getValidColumnTypes: (): Array<COLUMN_TYPE> => [
          ...STORABLE_BASE_COLUMN_TYPES,
          COLUMN_TYPE.LOCATION_SCHEMA_FIELD,
          ...getLocationCapacityConfigColumns(),
          COLUMN_TYPE.IGNORE,
        ],
        getRequiredColumnsError(columns: Array<SpreadsheetColumn>): string | null {
          if (columns.some(({type}) => type !== COLUMN_TYPE.IGNORE)) {
            return null;
          }
          return 'At least one column must be specified.';
        },
        onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForCreateLocations,
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: true,
        enableSkipErrors: true,
      }),
    },
  ];

  override render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="location"
        importType={IMPORT_TYPE.LOCATIONS}
        initialSettings={{initialRegistry: this.props.initialRegistry}}
        onImportFromSpreadsheet={SpreadsheetBulkImportActions.createLocationsFromSpreadsheet}
        analyticsMetadata={{actionOrigin: this.props.actionOrigin}}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Import locations"
      />
    );
  }
}

export default BulkCreateLocationsFlow;
